// Round-trip import of the Risk register (risks + assumptions + dependencies)
// from CSV / XLSX. Schema mirrors the export shape produced by `bidPack.js`.
import * as XLSX from "xlsx";
import { RISK_CATEGORIES, DEPENDENCY_TYPES } from "./s1000dData";

const BLOCKS = {
  risks: "RISKS",
  assumptions: "ASSUMPTIONS",
  dependencies: "DEPENDENCIES",
};

const VALID_CATEGORIES = new Set(RISK_CATEGORIES.map((c) => c.id));
const CATEGORY_LABEL_TO_ID = new Map(RISK_CATEGORIES.flatMap((c) => [
  [c.id.toLowerCase(), c.id],
  [c.label.toLowerCase(), c.id],
]));
const VALID_DEP_TYPES = new Set(DEPENDENCY_TYPES);

function norm(s) { return String(s || "").trim(); }
function lc(s) { return norm(s).toLowerCase(); }
function toInt(v, fallback = 0) {
  const n = parseInt(norm(v), 10);
  return Number.isFinite(n) ? n : fallback;
}
function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

async function readRows(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, blankrows: false });
}

function findBlock(rows, blockName) {
  for (let i = 0; i < rows.length; i++) {
    if (lc(rows[i]?.[0]) === blockName.toLowerCase()) return i;
  }
  return -1;
}

function sliceBlock(rows, startIdx) {
  const out = [];
  for (let i = startIdx + 2; i < rows.length; i++) {
    const row = rows[i] || [];
    const first = norm(row[0]);
    if (!first) break;
    if (/^[A-Z & /-]+$/.test(first) && row.filter((c) => norm(c) !== "").length <= 2) break;
    out.push(row);
  }
  return out;
}

// Read the header row that sits between the block label and the data
// (rows[startIdx+1]). Returns a case-insensitive name -> column-index map.
function headerMap(rows, startIdx) {
  const header = rows[startIdx + 1] || [];
  const map = {};
  header.forEach((cell, idx) => {
    const key = lc(cell);
    if (key && !(key in map)) map[key] = idx;
  });
  return map;
}

// Pick the first matching column index given a list of candidate header names.
function pick(hmap, ...names) {
  for (const n of names) {
    const idx = hmap[n.toLowerCase()];
    if (typeof idx === "number") return idx;
  }
  return -1;
}

function parseRisksBlock(rows) {
  const start = findBlock(rows, BLOCKS.risks);
  if (start === -1) return { risks: null, warnings: [], count: 0 };
  const dataRows = sliceBlock(rows, start);
  const risks = [];
  const warnings = [];
  for (const r of dataRows) {
    // Two layouts supported:
    // (a) Export shape: #, Title, Category, L, L-label, I, I-label, Score, Band, Mitigation, Owner
    // (b) Simple shape: Title, Category, L, I, Mitigation, Owner
    const c0 = norm(r[0]);
    const c1 = norm(r[1]);
    if (!c0 && !c1) continue;
    let title, category, likelihood, impact, mitigation, ownerRole;
    if (/^\d+$/.test(c0) && c1) {
      // Export layout
      title = norm(r[1]);
      category = norm(r[2]);
      likelihood = toInt(r[3]);
      impact = toInt(r[5]);
      mitigation = norm(r[9]);
      ownerRole = norm(r[10]);
    } else {
      // Simple layout
      title = norm(r[0]);
      category = norm(r[1]);
      likelihood = toInt(r[2]);
      impact = toInt(r[3]);
      mitigation = norm(r[4]);
      ownerRole = norm(r[5]);
    }
    if (!title) continue;
    const catId = CATEGORY_LABEL_TO_ID.get(lc(category)) || "technical";
    if (!VALID_CATEGORIES.has(catId)) {
      warnings.push(`Unknown risk category "${category}" — defaulting to technical`);
    }
    risks.push({
      id: `r${Date.now()}${risks.length}`,
      title,
      category: catId,
      likelihood: clamp(likelihood, 0, 5),
      impact: clamp(impact, 0, 5),
      mitigation,
      ownerRole,
    });
  }
  return { risks, warnings, count: risks.length };
}

function parseAssumptionsBlock(rows) {
  const start = findBlock(rows, BLOCKS.assumptions);
  if (start === -1) return { assumptions: null, count: 0 };
  const dataRows = sliceBlock(rows, start);
  const assumptions = [];
  for (const r of dataRows) {
    // Two columns supported: ("1", "text") or just ("text",...)
    const last = r.slice().reverse().find((c) => norm(c) !== "");
    const text = norm(last);
    if (text && !/^\d+$/.test(text)) {
      assumptions.push({ id: `a${Date.now()}${assumptions.length}`, text });
    }
  }
  return { assumptions, count: assumptions.length };
}

function parseDependenciesBlock(rows) {
  const start = findBlock(rows, BLOCKS.dependencies);
  if (start === -1) return { dependencies: null, warnings: [], count: 0 };
  const dataRows = sliceBlock(rows, start);
  const hmap = headerMap(rows, start);
  const typeIdx = pick(hmap, "type");
  const textIdx = pick(hmap, "text", "description", "detail");
  const hasHeader = typeIdx !== -1 || textIdx !== -1;
  const dependencies = [];
  const warnings = [];
  for (const r of dataRows) {
    let type, text;
    if (hasHeader) {
      type = typeIdx !== -1 ? norm(r[typeIdx]) : "";
      text = textIdx !== -1 ? norm(r[textIdx]) : "";
      if (!text) {
        // Fall back to last non-empty cell so a row with only id+text still works.
        const last = r.slice().reverse().find((c) => norm(c) !== "");
        text = norm(last);
      }
    } else {
      type = norm(r[0]);
      text = norm(r[1]);
      if (!text) {
        text = type;
        type = "Internal";
      }
    }
    if (!text) continue;
    if (!type) type = "Internal";
    if (!VALID_DEP_TYPES.has(type)) {
      warnings.push(`Unknown dependency type "${type}" — defaulting to Internal`);
      type = "Internal";
    }
    dependencies.push({ id: `d${Date.now()}${dependencies.length}`, text, type });
  }
  return { dependencies, warnings, count: dependencies.length };
}

export async function parseRiskImport(file) {
  if (!file) throw new Error("No file selected");
  const ext = (file.name || "").toLowerCase().split(".").pop();
  if (!["csv", "xlsx", "xls"].includes(ext)) {
    throw new Error(`Unsupported file type: .${ext}. Use .csv or .xlsx`);
  }
  let rows;
  try { rows = await readRows(file); }
  catch (err) { throw new Error(`Failed to parse file: ${err.message || err}`); }
  if (!rows || rows.length === 0) throw new Error("File is empty");

  const r = parseRisksBlock(rows);
  const a = parseAssumptionsBlock(rows);
  const d = parseDependenciesBlock(rows);

  if (!r.risks && !a.assumptions && !d.dependencies) {
    throw new Error("No 'RISKS', 'ASSUMPTIONS' or 'DEPENDENCIES' blocks found");
  }

  return {
    risks: r.risks, assumptions: a.assumptions, dependencies: d.dependencies,
    riskCount: r.count, assumptionsCount: a.count, dependenciesCount: d.count,
    warnings: [...(r.warnings || []), ...(d.warnings || [])],
  };
}

export function buildRiskTemplateXlsx() {
  const rows = [];
  rows.push(["S1000D Risk Register — Import Template"]);
  rows.push([]);
  rows.push([BLOCKS.risks]);
  rows.push(["Title", "Category", "Likelihood (1-5)", "Impact (1-5)", "Mitigation", "Owner"]);
  rows.push(["Source data quality / availability", "sourceData", 3, 4, "Run a 2-week source-data audit before authoring", "S1000D Specialist"]);
  rows.push(["Customer review cycle slippage", "schedule", 3, 3, "Lock SLA for review turn-around in contract", "Project Manager"]);
  rows.push([]);
  rows.push([BLOCKS.assumptions]);
  rows.push(["#", "Text"]);
  rows.push([1, "Customer supplies usable source data at kick-off"]);
  rows.push([2, "S1000D Issue 6.0 will apply for the duration of the contract"]);
  rows.push([]);
  rows.push([BLOCKS.dependencies]);
  rows.push(["Type", "Text"]);
  rows.push(["Customer", "SME availability for technical reviews"]);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Risks");
  return wb;
}

export function downloadRiskImportTemplate() {
  const wb = buildRiskTemplateXlsx();
  XLSX.writeFile(wb, "s1000d_risk_register_template.xlsx");
}

// S1000D Bike sample — realistic risk register, assumptions and dependencies
// for a small bicycle publication contract.  Designed to populate the Risk
// tab end-to-end for demos and regression testing.
export function buildBikeSampleRiskXlsx() {
  const rows = [];
  rows.push(["S1000D Bike — Sample Risk Register"]);
  rows.push(["Source: bike-themed example data for testing the risk importer."]);
  rows.push([]);
  rows.push([BLOCKS.risks]);
  rows.push(["Title", "Category", "Likelihood (1-5)", "Impact (1-5)", "Mitigation", "Owner"]);
  rows.push(["BOM and CAD drawings inconsistent across component versions", "sourceData", 3, 4, "Run a 2-week source-data audit; lock drawing revisions before authoring", "S1000D Specialist"]);
  rows.push(["Component manufacturer changes parts mid-contract", "technical", 3, 3, "Scope to current bike model only; freeze applicability tokens", "Project Manager"]);
  rows.push(["Customer review cycle slippage on procedural DMs", "schedule", 3, 4, "Lock review SLA in contract; weekly stand-ups with reviewer", "Project Manager"]);
  rows.push(["French and Spanish translation cycle runs late", "schedule", 2, 3, "Schedule translations in parallel with QA, not after publish", "Publication Manager"]);
  rows.push(["Illustration source files lost or corrupted", "quality", 2, 3, "Git-backed source for all SVG/CAD inputs; nightly backup", "Illustrator"]);
  rows.push(["SME for advanced maintenance procedures unavailable", "resource", 2, 4, "Identify alternate SME at kick-off; book sessions in advance", "Project Manager"]);
  rows.push(["BREX rule conflicts with customer style guide", "technical", 2, 3, "Walk through BREX with customer in week 1; resolve before authoring", "S1000D Specialist"]);
  rows.push([]);
  rows.push([BLOCKS.assumptions]);
  rows.push(["#", "Text"]);
  rows.push([1, "Bike model 'BIKE01A' supplied at kick-off with full CAD and BOM"]);
  rows.push([2, "S1000D Issue 6.0 applies for the duration of the contract"]);
  rows.push([3, "Source language is English; French and Spanish translations are deliverables"]);
  rows.push([4, "Customer provides one named technical reviewer with 4-day-per-week availability"]);
  rows.push([5, "Illustrations are produced from supplied CAD; no new field photography"]);
  rows.push([]);
  rows.push([BLOCKS.dependencies]);
  rows.push(["ID", "Type", "Text"]);
  rows.push(["d1", "Customer", "BOM and CAD files at kick-off (week 0)"]);
  rows.push(["d2", "Customer", "Named SME for maintenance procedures (weeks 2-10)"]);
  rows.push(["d3", "Customer", "Style guide and visual identity assets (week 1)"]);
  rows.push(["d4", "Internal", "Illustrator licensing for SVG production renewed before kick-off"]);
  rows.push(["d5", "Supplier", "Translation vendor onboarded by week 8"]);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Bike Risks");
  return wb;
}

export function downloadBikeSampleRisks() {
  XLSX.writeFile(buildBikeSampleRiskXlsx(), "s1000d_bike_sample_risks.xlsx");
}

export function mergeRiskPlan(existing, incoming) {
  return {
    ...existing,
    risks: [...(existing.risks || []), ...(incoming.risks || [])],
    assumptions: [...(existing.assumptions || []), ...(incoming.assumptions || [])],
    dependencies: [...(existing.dependencies || []), ...(incoming.dependencies || [])],
  };
}
